import { useEffect, useRef, type RefObject } from "react";
import { useMutationState } from "@tanstack/react-query";

import { Banner } from "@/components/ui/banner";
import { ApiError } from "@/lib/api-client";
import {
  SEAT_REQUEST_MUTATION_KEY,
  useRequestAdditionalSeats,
} from "@/hooks/queries/useRequestAdditionalSeats";
import type { SeatRequestAcceptedData } from "@/hooks/queries/seat-request-types";
import {
  toSeatRequestBanner,
  type SeatRequestBanner as SeatRequestBannerCopy,
} from "@/routes/org-admin/seat-request-error";

// The Story 14.7 one-step seat-request CTA (FR77/FR78), extracted at Story
// 14.11 so the dashboard (Screen 25) and Seats & allocation (Screen 26) mount
// the SAME control rather than two drifting copies. Behaviour is unchanged
// from the dashboard's original inline block — the extraction is a move, not a
// redesign:
//
//   * ALL session state is derived from the mutation cache (gcTime: Infinity),
//     not component state, so one click on either screen disables the CTA on
//     BOTH for the rest of the SPA session and survives navigation; sign-out's
//     queryClient.clear() is the wipe chokepoint.
//   * `exact: true` on every filter — v5 mutation filters prefix-match, so a
//     future sibling key must not corrupt this guard.
//   * A non-rate-limit error stays INSTANCE-local (the CTA is its own retry
//     affordance): it is read from `mutation.isError`, not from the cache, so
//     it disappears on remount exactly as it did before the extraction.
//   * Focus moves to the confirmation banner only when THIS instance's
//     mutation succeeded — a remount that merely re-renders the persisted
//     banner must not steal focus.

export interface SeatRequestControl {
  /** Fire the one-step POST. Idempotent against double clicks and re-entry. */
  request: () => void;
  /** CTA disabled state: in flight anywhere, or already requested this session. */
  disabled: boolean;
  confirmationMessage: string | null;
  confirmationRef: RefObject<HTMLDivElement | null>;
  errorBanner: SeatRequestBannerCopy | null;
}

export function useSeatRequest(): SeatRequestControl {
  const seatRequest = useRequestAdditionalSeats();

  // The select optional-chains: a drifted empty 202 must degrade, not crash
  // the host screen's render.
  const confirmations = useMutationState({
    filters: {
      mutationKey: SEAT_REQUEST_MUTATION_KEY,
      status: "success",
      exact: true,
    },
    select: (mutation) =>
      (mutation.state.data as SeatRequestAcceptedData | undefined)?.message,
  });
  const confirmationMessage = confirmations[confirmations.length - 1] ?? null;

  // A POST still in flight in ANY instance — without this, navigating away and
  // back mid-request re-enables the CTA and a second click would be ADMITTED by
  // the backend budget (duplicate Director email).
  const seatRequestPending =
    useMutationState({
      filters: {
        mutationKey: SEAT_REQUEST_MUTATION_KEY,
        status: "pending",
        exact: true,
      },
      select: (mutation) => mutation.state.submittedAt,
    }).length > 0;

  // 429 ORG-004 means a request WAS admitted inside the current window (the
  // limiter records only admitted attempts), so it counts as "already
  // requested" for the session-disable too. Read the LATEST error entry —
  // gcTime: Infinity lets failed mutations accumulate.
  const errors = useMutationState({
    filters: {
      mutationKey: SEAT_REQUEST_MUTATION_KEY,
      status: "error",
      exact: true,
    },
    select: (mutation) => mutation.state.error,
  });
  const lastError = errors[errors.length - 1] ?? null;
  const rateLimitError =
    lastError instanceof ApiError && lastError.code === "ORG-004"
      ? lastError
      : null;

  const alreadyRequested = confirmationMessage != null || rateLimitError != null;

  // In-flight ref, not isPending: two same-tick clicks would both read the
  // stale pending state before React re-renders.
  const seatRequestInFlight = useRef(false);
  const request = () => {
    if (seatRequestInFlight.current || seatRequestPending || alreadyRequested)
      return;
    seatRequestInFlight.current = true;
    seatRequest.mutate(undefined, {
      onSettled: () => {
        seatRequestInFlight.current = false;
      },
    });
  };

  // Success flips the focused button to disabled, which drops keyboard/SR focus
  // onto <body> — move it to the confirmation banner instead.
  const confirmationRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (seatRequest.isSuccess) confirmationRef.current?.focus();
  }, [seatRequest.isSuccess]);

  const visibleError =
    rateLimitError ?? (seatRequest.isError ? seatRequest.error : null);

  return {
    request,
    disabled: seatRequestPending || alreadyRequested,
    confirmationMessage,
    confirmationRef,
    errorBanner:
      !confirmationMessage && visibleError
        ? toSeatRequestBanner(visibleError)
        : null,
  };
}

/** The confirmation / error banner pair for {@link useSeatRequest}. */
export function SeatRequestBanner({ control }: { control: SeatRequestControl }) {
  if (control.confirmationMessage) {
    return (
      <Banner
        ref={control.confirmationRef}
        tabIndex={-1}
        variant="success"
        title={control.confirmationMessage}
      />
    );
  }
  if (control.errorBanner) {
    return (
      <Banner
        variant={control.errorBanner.variant}
        title={control.errorBanner.title}
      >
        {control.errorBanner.body}
      </Banner>
    );
  }
  return null;
}
